import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Button,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import { Edit, Delete, Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AdminNavbar from "../components/AdminNavbar";
import Sidebar from "../components/Sidebar";
import ApiService from "../../../config/api";

const Localities = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [sidebarOpen, setSidebarOpen] = useState(!isSmallScreen);
  const [localities, setLocalities] = useState([]);
  const [deleteId, setDeleteId] = useState(null); // track which locality to delete
  const [openDialog, setOpenDialog] = useState(false);

  const navigate = useNavigate();

  const toggleDrawer = () => setSidebarOpen(!sidebarOpen);

  // ✅ Fetch Localities
  const fetchLocalities = async () => {
    try {
      const res = await ApiService.get("localities");
      if (!res.data.error) {
        setLocalities(res.data.meta);
      }
    } catch (err) {
      console.error("Error fetching localities:", err);
    }
  };

  // ✅ Confirm Delete
  const confirmDelete = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  // ✅ Perform Delete
  const handleDelete = async () => {
    try {
      await ApiService.delete(`localities/remove/${deleteId}`);
      fetchLocalities(); // refresh list
      setOpenDialog(false);
      setDeleteId(null);
    } catch (err) {
      console.error("Error deleting locality:", err);
    }
  };

  useEffect(() => {
    fetchLocalities();
  }, []);

  return (
    <Box
      display="grid"
      gridTemplateColumns={sidebarOpen ? "200px 1fr" : "60px 1fr"}
      sx={{
        transition: "grid-template-columns 0.3s ease-in-out",
        backgroundColor: "var(--bs-secondary)",
        minHeight: "100vh",
      }}
    >
      <Sidebar
        open={sidebarOpen}
        toggleDrawer={toggleDrawer}
        setOpen={setSidebarOpen}
      />

      <Box>
        <AdminNavbar />
        <Box sx={{ padding: 2 }}>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            m={2}
          >
            <Typography
              variant="h6"
              sx={{ color: "var(--bs-accent)", fontWeight: "bold" }}
            >
              All Localities
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate("/admin/create-locality")}
              sx={{
                backgroundColor: "var(--bs-primary)",
                color: "#fff",
                "&:hover": { backgroundColor: "var(--bs-accent)" },
              }}
            >
              Add New Locality
            </Button>
          </Box>

          {/* Cards */}
          <Grid container spacing={3}>
            
            {localities.map((locality) => (
              <Grid item xs={12} sm={6} md={4} key={locality._id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: 3,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                    backgroundColor: "var(--bs-primary)",
                  }}
                >
                  {/* Image */}
                  {locality.image && (
                    <CardMedia
                      component="img"
                      image={locality.image}
                      alt={locality.name}
                      sx={{
                        height: 180, // fixed uniform height
                        width: "100%",
                        objectFit: "cover", // keeps aspect ratio
                        backgroundColor: "#f5f5f5",
                      }}
                    />
                  )}

                  {/* Content */}
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      noWrap
                      sx={{
                        color: "var(--bs-accent)",
                        fontWeight: 600,
                        textAlign: "start",
                      }}
                    >
                      {locality.name}
                    </Typography>
                  </CardContent>

                  {/* Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1,
                      px: 1,
                      pb: 1,
                    }}
                  >
                    <IconButton
                      onClick={() =>
                        navigate("/admin/create-locality", {
                          state: {
                            isEdit: true,
                            id: locality._id,
                            name: locality.name,
                            image: locality.image,
                          },
                        })
                      }
                      sx={{ color: "var(--bs-accent)" }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => confirmDelete(locality._id)}
                      sx={{ color: "var(--bs-accent)" }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this locality? This action cannot be
            undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default Localities;
